import { useCallback, useState } from "react"
import { uniqueKey } from "utils/uniqueKey"
import { useDeepMemo } from "utils/useDeepMemo"

type InitialObject<T> = {
  [K in keyof T]: T[keyof T]
}

export type ListElement<T> = T & { key: string }

export type List<T> = ListElement<T>[]

export type ChangeElement<T> = <K extends keyof T>(key: string, name: K, value: T[K]) => void

interface IUseHandleList<T> {
  list: List<T>
  addElement: (obj?: T) => void
  removeElement: (key: string) => void
  changeElement: ChangeElement<T>
  resetList: () => void
  replaceList: (array: T[]) => void
  addElementAtBeginning: (obj?: T) => void
  addElementAtEnd: (obj?: T) => void
}

/**
 * Custom React hook for managing a list of objects with unique keys.
 *
 * @template T - The type of the objects in the list.
 * @param {T} initialObj - The initial object that serves as a blueprint for the list items.
 * @returns {IUseHandleList<T>} An object containing functions and state to manage the list.
 */

export const useHandleObjList = <T extends InitialObject<T>>(initialObj: T): IUseHandleList<T> => {
  // State to hold the list of objects.
  const [list, setList] = useState<List<T>>([
    {
      ...initialObj,
      key: uniqueKey()
    }
  ])

  // Private method to create a new object with a unique key.
  const createNewObj = useCallback((obj: T) => {
    return {
      ...obj,
      key: uniqueKey()
    }
  }, [])

  /**
   * Adds a new element to the list using the provided object or the initial object.
   *
   * @param {T} [obj=initialObj] - The object to add to the list.
   */
  const addElement = useCallback(
    (obj: T = initialObj) => {
      setList((prev) => [...prev, createNewObj(obj)])
    },
    [createNewObj, initialObj]
  )

  /**
   * Changes a property of a list element identified by its key.
   *
   * @param {string} key - The key of the element to modify.
   * @param {string} name - The name of the property to change.
   * @param {*} value - The new value of the property.
   */
  const changeElement: ChangeElement<T> = useCallback((key, name, value) => {
    setList((prevState) => {
      const newState = prevState.map((element) => {
        if (element.key === key) {
          return { ...element, [name]: value }
        }

        return element
      })

      return newState
    })
  }, [])

  /**
   * Removes an element from the list based on its key.
   *
   * @param {string} key - The key of the element to remove.
   */
  const removeElement = useCallback((key: string) => {
    setList((prevState) => {
      const newState = prevState.filter((element) => element.key !== key)

      return newState
    })
  }, [])

  /**
   * Resets the list to contain a single element with the initial values.
   */
  const resetList = useCallback(() => {
    setList([
      {
        ...initialObj,
        key: uniqueKey()
      }
    ])
  }, [initialObj])

  const replaceList = useCallback((array: T[]) => {
    const newArrayWithKey = array.map((element) => {
      return {
        ...element,
        key: uniqueKey()
      }
    })

    setList(() => newArrayWithKey)
  }, [])

  const memoizedList = useDeepMemo(() => list, [list])

  const addElementAtBeginning = useCallback(
    (obj: T = initialObj) => {
      setList((prev) => [createNewObj(obj), ...prev])
    },
    [createNewObj, initialObj]
  )

  const addElementAtEnd = useCallback(
    (obj: T = initialObj) => {
      setList((prev) => [...prev, createNewObj(obj)])
    },
    [createNewObj, initialObj]
  )

  return {
    list: memoizedList,
    addElement,
    changeElement,
    removeElement,
    resetList,
    replaceList,
    addElementAtBeginning,
    addElementAtEnd
  }
}
